import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { Stack, FormControl, InputLabel, Select, MenuItem, CircularProgress, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useReservationApis } from '../../../apis/ReservationAPIs.js';
import { useInventoryAPIs } from '../../../apis/InventoryAPIs.js';
import { shouldDisableDate } from '../Utils/ReservationTimeDateHelper.js';
import { useStudentApis } from '../../../apis/StudentAPIs.js';
import { 
  addAssetAvailability, 
  getCommonAvailableTimes, 
  clearAllAssetAvailability
} from '../Utils/MultiAssetAvailabilityHelper.js';

export default function CreateReservationDialog({
  open,
  onClose,
  onSave,
  data = {},
  useDropDown = false // false is the default, for reservation variant
}) {
  const { fetchAvailableTimes, addMultipleReservations, addMultipleStudentReservations } = useReservationApis();
  const { fetchInventory } = useInventoryAPIs();
  const { getSimplifiedStudentInfoById } = useStudentApis(); 
  const userRole = sessionStorage.getItem('userRole');
  const [mode, setMode] = useState('student');

  // Inventory state (only if useInventory)
  const [inventoryList, setInventoryList] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(true);

  // Time slots
  const [availableTime, setAvailableTime] = useState([]);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [lastFetchedDate, setLastFetchedDate] = useState(null);

  const formatKitItems = (kitItems = []) =>
      kitItems
          .map(k => {
              // Fallback logic: Some kit items may have a 'description' property, others may use 'item'.
              // This checks for 'description' first, then 'item', and defaults to an empty string if neither is present.
              const desc = k.description || k.item || '';
              const model = k.model ? ` (${k.model})` : '';
              return `${desc}${model}`;
          })
          .join(', ');

  // Submitting
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reservation state (common fields)
  const [reservation, setReservation] = useState({
    assetTags: data?.tag ? (Array.isArray(data.tag) ? data.tag : [data.tag]) : [],
    userId: sessionStorage.getItem('userId'),
    checkOut: null,
    checkOutTime: null
  });

  // Student-specific fields as separate state
  const [studentId, setStudentId] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Asset selection dialog state
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);

  // Load inventory if dropdown variant
  useEffect(() => {
    if (!useDropDown) return;
    let mounted = true;
    (async () => {
      try {
        const items = await fetchInventory();
        if (mounted) setInventoryList(items || []);
      } catch {
        if (mounted) setInventoryList([]);
      } finally {
        if (mounted) setLoadingInventory(false);
      }
    })();
    return () => { mounted = false; };
  }, [useDropDown]);

  // Cleanup asset availability when component unmounts
  useEffect(() => {
    return () => {
      clearAllAssetAvailability();
    };
  }, []);

  // Fetch available times on date or asset change
  useEffect(() => {
    const fetchSlots = async () => {
      if (!reservation.checkOut || reservation.assetTags.length === 0) {
        setAvailableTime([]);
        return;
      }
      const dateStr = reservation.checkOut.format('YYYY-MM-DD');
      if (lastFetchedDate === dateStr) return;
      setIsLoadingTimes(true);
      
      try {
        // Clear previous availability data
        clearAllAssetAvailability();
        
        // Fetch availability for each selected asset
        const fetchPromises = reservation.assetTags.map(assetTag => 
          fetchAvailableTimes(dateStr, assetTag)
        );
        
        const results = await Promise.all(fetchPromises);
        
        // Add each asset's availability to the tracking system
        results.forEach((slots, index) => {
          const assetTag = reservation.assetTags[index];
          if (slots && slots.length > 0) {
            addAssetAvailability(assetTag, slots);
          }
        });
        
        // Get common available times
        const commonTimes = getCommonAvailableTimes();
        setAvailableTime(commonTimes);
        setLastFetchedDate(dateStr);
      } catch {
        setAvailableTime([]);
      } finally {
        setIsLoadingTimes(false);
      }
    };
    const timer = setTimeout(fetchSlots, 300);
    return () => clearTimeout(timer);
  }, [reservation.checkOut, reservation.assetTags, lastFetchedDate]);

  const handleModeChange = e => {
    const newMode = e.target.value;
    setMode(newMode);
    if (newMode === 'staff/admin') {
      setStudentId('');
      setFirstName('');
      setReservation(prev => ({
        ...prev,
        userId: sessionStorage.getItem('userId')
      }));
    } else {
      setReservation(prev => ({ ...prev, userId: null }));
    }
  };

  const handleSave = async () => {
    const missingCommon =
      reservation.assetTags.length === 0 ||
      !reservation.checkOut ||
      !reservation.checkOutTime;

    if (userRole !== 'ROLE_STUDENT') {
      const missingStudent =
        mode === 'student' && (!studentId.trim() || !firstName.trim());
      const missingStaff =
        mode === 'staff/admin' && !reservation.userId;

      if (missingCommon || missingStudent || missingStaff) {
        alert('Please fill all required fields');
        return;
      }
    } else {
      if (missingCommon) {
        alert('Please fill all required fields');
        return;
      }
    }


    setIsSubmitting(true);

    try {
      // Backend will handle availability checking and check-in time calculation
      const dateStr = reservation.checkOut.format('YYYY-MM-DD');

      // Send only checkout date/time and asset tags - backend will calculate check-in time
      const baseData = {
        checkOut: dateStr,
        checkOutTime: dayjs(reservation.checkOutTime, 'h:mm A').format('HH:mm:ss'),
        reservationDate: dayjs().format('YYYY-MM-DD')
        // Note: checkIn and checkInTime are calculated by the backend based on asset's allowedTime
      };

      // Create all reservation data at once
      const allReservations = reservation.assetTags.map(assetTag => ({
        ...baseData,
        assetTag: assetTag,
        userId: mode === 'student' && userRole !== 'ROLE_STUDENT' ? null : reservation.userId
      }));

      let result;
      if (mode === 'student' && userRole !== 'ROLE_STUDENT') {
        result = await addMultipleStudentReservations(studentId, allReservations);
      } else {
        result = await addMultipleReservations(allReservations);
      }

      // Check if reservation was successful
      if (result.success) {
        alert(
          result.partialSuccess
            ? 'All reservations saved, but some emails failed to send.'
            : 'All reservations saved and confirmation emails sent!'
        );
        onSave();
      } else if (result.studentExist === false) {
        alert('Student not found, try again or add student in the student tab.');
      } else if (result.isAvailabilityError) {
        // Handle backend availability validation errors
        let errorMessage = `Backend validation failed:\n\n`;
        errorMessage += `${result.message}\n\n`;
        
        if (result.unavailableAssets && result.unavailableAssets.length > 0) {
          // Convert asset tags to names for better user experience
          const unavailableAssetNames = result.unavailableAssets.map(tag => {
            const assetName = inventoryList.find(item => item.tag === tag)?.item || `Asset ${tag}`;
            return assetName;
          });
          errorMessage += `Unavailable assets: ${unavailableAssetNames.join(', ')}`;
        }
        
        alert(errorMessage);
      } else {
        alert('Failed to save reservations.');
      }
    } catch (err) {
      console.error(err);
      alert('An error occurred while processing the reservation.');
    } finally {
      setIsSubmitting(false);
      setReservation(prev => ({
        ...prev,
        checkOut: null,
        checkOutTime: null
      }));
      if (mode === 'student') {
        setStudentId('');
        setFirstName('');
      }
      // Clear asset availability data
      clearAllAssetAvailability();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Reservation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>

            {/* Mode selector for non-students */}
            {userRole !== 'ROLE_STUDENT' && (
              <FormControl fullWidth variant="standard">
                <InputLabel id="mode-select-label">Reserving For</InputLabel>
                <Select
                  labelId="mode-select-label"
                  value={mode}
                  onChange={handleModeChange}
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="staff/admin">Staff/Admin</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Student fields */}
            {mode === 'student' && userRole !== 'ROLE_STUDENT' && (
              <>
                <TextField
                  label="Student ID"
                  variant="standard"
                  type="number"
                  value={studentId}
                  onChange={e => {
                    if (/^\d*$/.test(e.target.value)) {
                      setStudentId(e.target.value);
                    }
                  }}
                  onBlur={async () => {
                    if (studentId.trim()) {
                      try {
                        const res = await getSimplifiedStudentInfoById(studentId.trim());
                        if (res.success) {
                          setFirstName(res.firstName);
                          setLastName(res.lastName);
                        } else {
                          setFirstName('');
                          setLastName('');
                          alert(res.message);
                        }
                      } catch (err) {
                        console.error(err);
                        alert('Failed to fetch student information.');
                      }
                    }
                  }}
                  fullWidth
                />
                <TextField
                  label="First Name"
                  variant="standard"
                  disabled
                  value={firstName}
                  fullWidth
                />
                <TextField
                  label="Last Name"
                  variant="standard"
                  disabled
                  value={lastName}
                  fullWidth
                />
              </>
            )}

            {/* Asset selector */}
             {useDropDown ? (
               <>
                 <FormControl fullWidth disabled={loadingInventory} variant="standard">
                                       <div
                      onClick={() => setAssetDialogOpen(true)}
                      className="asset-selector-button"
                    >
                      <span className={`asset-selector-text ${reservation.assetTags.length === 0 ? 'placeholder' : ''}`}>
                        {reservation.assetTags.length > 0 
                          ? `${reservation.assetTags.length} asset(s) selected: ${reservation.assetTags.map(assetTag => {
                              const inventoryItem = inventoryList.find(item => item.tag === assetTag);
                              return inventoryItem ? inventoryItem.item : `Asset ${assetTag}`;
                            }).join(', ')}`
                          : 'Click to select assets'
                        }
                      </span>
                      <span className="asset-selector-arrow">▼</span>
                    </div>
                 </FormControl>

                 {/* Asset Selection Dialog */}
                 <Dialog 
                   open={assetDialogOpen} 
                   onClose={() => setAssetDialogOpen(false)}
                   maxWidth="md" 
                   fullWidth
                 >
                   <DialogTitle>
                     Select Assets for Reservation
                                           <Button
                        onClick={() => setAssetDialogOpen(false)}
                        className="asset-dialog-close-button"
                      >
                        ✕
                      </Button>
                   </DialogTitle>
                   <DialogContent>
                                           <div className="asset-instructions-container">
                        <p className="asset-instructions-title"><strong>Quick guide:</strong></p>
                        <ul className="asset-instructions-list">
                          <li>Click to select assets</li>
                          <li>Green = selected</li>
                          <li>Click "Done" when ready</li>
                        </ul>
                      </div>
                     
                                           <div className="asset-list-container">
                        {loadingInventory ? (
                          <div className="asset-loading-container">
                            <CircularProgress />
                            <p>Loading assets...</p>
                          </div>
                        ) : inventoryList.length === 0 ? (
                          <div className="asset-empty-container">
                            No assets available
                          </div>
                        ) : (
                         inventoryList.map(item => {
                           const kitsText = formatKitItems(item.kitItems);
                           const isSelected = reservation.assetTags.includes(item.tag);
                           return (
                                                           <div
                                key={item.tag}
                                onClick={() => {
                                  const newAssetTags = isSelected
                                    ? reservation.assetTags.filter(tag => tag !== item.tag)
                                    : [...reservation.assetTags, item.tag];
                                  setReservation(prev => ({
                                    ...prev,
                                    assetTags: newAssetTags,
                                    checkOut: null,
                                    checkOutTime: null
                                  }));
                                  setLastFetchedDate(null);
                                }}
                                className={`asset-item ${isSelected ? 'selected' : ''}`}
                              >
                                                               <div className={`asset-item-name ${isSelected ? 'selected' : ''}`}>
                                  {item.item}
                                </div>
                                <div className="asset-item-description">
                                  {item.typeDescription}
                                </div>
                                {kitsText && (
                                  <div className="asset-item-kit">
                                    Items: {kitsText}
                                  </div>
                                )}
                                {isSelected && (
                                  <div className="asset-item-selected-indicator">
                                    ✓ Selected
                                  </div>
                                )}
                             </div>
                           );
                         })
                       )}
                     </div>
                   </DialogContent>
                   <DialogActions>
                     <Button onClick={() => setAssetDialogOpen(false)} variant="contained">
                       Done
                     </Button>
                   </DialogActions>
                 </Dialog>
               </>
             ) : (
              <TextField
                label="Asset Tag"
                variant="standard"
                value={reservation.assetTags[0] || ''}
                onChange={e => {
                  const tag = e.target.value.trim();
                  setReservation(prev => ({ ...prev, assetTags: tag ? [tag] : [] }));
                }}
                fullWidth
                disabled
              />
            )}


            {/* Date picker */}
            <DatePicker
              label="Check-Out Date"
              value={reservation.checkOut}
              onChange={newDate =>
                setReservation(prev => ({
                  ...prev,
                  checkOut: newDate,
                  checkOutTime: null
                }))
              }
              minDate={dayjs()}
              shouldDisableDate={shouldDisableDate}
              renderInput={params => <TextField {...params} fullWidth />}
            />

                         {/* Selected assets info */}
             {reservation.assetTags.length > 0 && (
               <div className="selected-assets-info">
                 {process.env.NODE_ENV === 'development' && (
                   <small className="development-info">
                     Common times found: {availableTime.length} available
                   </small>
                 )}
               </div>
             )}

            {/* Time selector */}
            <FormControl fullWidth disabled={!reservation.checkOut || reservation.assetTags.length === 0} variant="standard">
              <InputLabel id="time-select-label">Check-Out Time</InputLabel>
              <Select
                labelId="time-select-label"
                value={reservation.checkOutTime || ''}
                onChange={e =>
                  setReservation(prev => ({ ...prev, checkOutTime: e.target.value }))
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 175 } } }}
                IconComponent={() =>
                  isLoadingTimes ? <CircularProgress size={24} /> : null
                }
              >
                {isLoadingTimes ? (
                  <MenuItem disabled>Loading available times...</MenuItem>
                ) : availableTime.length === 0 ? (
                  <MenuItem value="" disabled>
                    {reservation.assetTags.length === 0 
                      ? 'Please select assets first' 
                      : 'No common times available for selected assets'
                    }
                  </MenuItem>
                ) : (
                  availableTime.map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
